import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import useRequest from "@entrilia/shared/hooks/useRequest";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import biApi from "../../../../../api/biApi";
import type { Dashboard, SmartViewWidget } from "../../../../../api/types/dashboardsTypes";
import type { SelectedCompany, SelectedCompanyDashboard, SelectedCompanyTile } from "../../hooks/validationStateSlice";
import validationStateSlice, { actions } from "../../hooks/validationStateSlice";
import { isSmartViewWidget } from "../../models/isTileObjectSmartView";

type ActiveTileIdentifiers =
  | {
      companyCode: string;
      dashboardId: string;
      tileId: string;
    }
  | undefined;

const useDashboardValidation = (dashboards: Dashboard[]) => {
  const [state, dispatch] = useReducer(validationStateSlice.reducer, validationStateSlice.getInitialState());
  const { request: loadReports } = useRequest(biApi.dashboardReports.getReports);
  const [activeTileIdentifiers, setActiveTileIdentifiers] = useState<ActiveTileIdentifiers>(undefined);

  const companiesRef = useRef<SelectedCompany[]>([]);
  useEffect(() => {
    companiesRef.current = state.companies;
  }, [state.companies]);

  const activeCompanyTile = useMemo(() => {
    if (!activeTileIdentifiers) {
      return undefined;
    }

    return state.companies
      .flatMap((c) => c.dashboards)
      .flatMap((d) => d.tiles)
      .find(
        (t) =>
          t.companyCode === activeTileIdentifiers.companyCode &&
          t.tile.tileId === activeTileIdentifiers.tileId &&
          t.dashboard.dashboardId === activeTileIdentifiers.dashboardId
      );
  }, [state.companies, activeTileIdentifiers]);

  const loadReportsInfo = useCallback(async () => {
    const reportsInfo = dashboards
      .flatMap((c) => c.tiles)
      .map((c) => c.widget)
      .filter((w): w is SmartViewWidget => isSmartViewWidget(w))
      .map((w) => w.reportId);

    if (reportsInfo.length > 0) {
      const [response] = await loadReports(...reportsInfo);
      if (response) {
        dispatch(actions.setReportsInfoAction(response));
      }
    }
  }, [dashboards, loadReports]);

  useEffect(() => {
    if (!state.reportsInfoLoaded) {
      loadReportsInfo();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const reportsToValidate = useMemo(() => {
    if (!state.reportsInfoLoaded) {
      return [];
    }
    const tiles = state.companies
      .flatMap((c) => c.dashboards)
      .flatMap((c) => c.tiles)
      .filter((c) => c.validated === false && c.validating === false && c.error !== true);

    return tiles;
  }, [state.companies, state.reportsInfoLoaded]);

  const validateTile = useCallback((tile: SelectedCompanyTile) => {
    if (!isSmartViewWidget(tile.tile.widget)) {
      dispatch(
        actions.updateTileAction({
          dashboard: tile.dashboard,
          tile,
          changes: { validating: false, validated: true, error: false },
        })
      );
      return;
    }

    if (!tile.report) {
      dispatch(
        actions.updateTileAction({
          dashboard: tile.dashboard,
          tile,
          changes: { validating: false, validated: true, error: true },
        })
      );
      return;
    }

    dispatch(actions.updateTileAction({ dashboard: tile.dashboard, tile, changes: { validating: true } }));
    const ct = biApi.dashboardReports.validateReport(
      {
        reportId: tile.report.reportId,
        fromCompanyCode: tile.report.clientCode,
        toCompanyCode: tile.companyCode,
        validateGroup: false,
      },
      (resp) => {
        const changes: Partial<SelectedCompanyTile> = { validating: false };
        if (!resp.success || resp.error) {
          changes.error = true;
        } else {
          changes.validated = true;
          changes.result = resp.data;
        }
        dispatch(actions.updateTileAction({ dashboard: tile.dashboard, tile, changes }));
      },
      () => {
        dispatch(
          actions.updateTileAction({
            dashboard: tile.dashboard,
            tile,
            changes: { validating: false, validated: false, error: true },
          })
        );
      }
    );
    dispatch(actions.updateTileAction({ dashboard: tile.dashboard, tile, changes: { ct } }));
  }, []);

  useEffect(() => {
    const reports = companiesRef.current.flatMap((c) => c.dashboards).flatMap((d) => d.tiles);
    const inProgressCount = reports.filter((r) => r.validating === true).length;
    const toValidate = reportsToValidate.filter((r) => !r.validating && !r.validated && !r.error);
    if (inProgressCount < 3 && toValidate.length > 0) {
      validateTile(defined(toValidate[0]));
    }
  }, [reportsToValidate, validateTile]);

  const updateCompanySelection = useCallback(
    (companyCodes: string[]) => {
      dispatch(actions.setCompaniesAction({ companyCodes, dashboards }));
    },
    [dashboards]
  );

  const removeCompany = useCallback((companyCode: string) => {
    dispatch(actions.removeCompanyAction(companyCode));
  }, []);

  const removeDashboard = useCallback((companyDashboard: SelectedCompanyDashboard) => {
    dispatch(actions.removeDashboardAction({ companyDashboard }));
  }, []);

  const removeTile = useCallback((tile: SelectedCompanyTile) => {
    dispatch(actions.removeTileAction({ tile }));
  }, []);

  const updateCompanyReport = useCallback((tile: SelectedCompanyTile, changes: Partial<SelectedCompanyTile>) => {
    dispatch(actions.updateTileAction({ dashboard: tile.dashboard, tile, changes }));
  }, []);

  const retryValidation = useCallback((tile: SelectedCompanyTile) => {
    dispatch(
      actions.updateTileAction({
        dashboard: tile.dashboard,
        tile,
        changes: {
          validated: false,
          validating: false,
          error: false,
        },
      })
    );
  }, []);

  const setActiveCompanyTile = useCallback((tile: SelectedCompanyTile) => {
    setActiveTileIdentifiers({
      companyCode: tile.companyCode,
      dashboardId: tile.dashboard.dashboardId,
      tileId: tile.tile.tileId,
    });
  }, []);

  return {
    selectedCompanies: state.companies,
    updateCompanySelection,
    removeCompany,
    updateCompanyReport,
    retryValidation,
    removeDashboard,
    removeTile,
    activeCompanyTile,
    setActiveCompanyTile,
  };
};

export default useDashboardValidation;
export type DashboardValidationType = ReturnType<typeof useDashboardValidation>;
